import { useId, useMemo, useRef, type TouchEventHandler } from 'react'
import cn from 'clsx'
import { formatSeconds } from './helpers/formatSeconds'
import { getWaveformPath } from './helpers/getWaveformPath'
import { useWaveformCursor } from './helpers/useWaveformCursor'
import { useWaveformDimensions } from './helpers/useWaveformDimensions'
import { type TWaveformProps } from './Waveform.types'
import styles from './Waveform.module.css'

// Distinguishes a scrub (horizontal drag) from a page-scroll (vertical) on touch devices.
const TAP_MOVE_THRESHOLD = 10

type TWaveformProgressProps = {
    id: string
    progress: number
}

const WaveformProgress = ({ id, progress }: TWaveformProgressProps) => (
    <defs>
        <linearGradient id={id} x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" className={styles.stopPlayback} />
            <stop offset={`${progress}%`} className={styles.stopPlayback} />
            <stop offset={`${progress}%`} className={styles.stopDefault} />
            <stop offset="100%" className={styles.stopDefault} />
        </linearGradient>
    </defs>
)

export const Waveform = ({
    peaks,
    progress,
    variant = 'dark',
    className,
    durationInSec,
    onSeek,
    onScrub = onSeek,
}: TWaveformProps) => {
    const gradientId = useId()
    const showProgress = progress > 0
    const isInteractive = Boolean(onSeek && durationInSec)

    const wrapperRef = useRef<HTMLDivElement>(null)
    const timeLabelRef = useRef<HTMLDivElement>(null)
    const touchStartRef = useRef<{ x: number; y: number } | null>(null)
    const isSwipingRef = useRef(false)

    // Measured unconditionally: the path geometry depends on the rendered box, not just seeking.
    const [width, height] = useWaveformDimensions(wrapperRef)
    const [showCursor, cursorOffsetX] = useWaveformCursor(wrapperRef, isInteractive)

    const d = useMemo(() => getWaveformPath(width, height, peaks), [width, height, peaks])

    const cursorSeconds =
        showCursor && width > 0 && durationInSec ? (cursorOffsetX * durationInSec) / width : 0

    // Flip the label to the cursor's left when it would overflow the right edge.
    const labelWidth = timeLabelRef.current?.offsetWidth ?? 0
    const cursorTimePosition =
        width - cursorOffsetX > labelWidth ? cursorOffsetX : cursorOffsetX - labelWidth

    const callAtOffset = (handler: TWaveformProps['onSeek'], offsetX: number) => {
        if (handler && durationInSec && width > 0) {
            handler((offsetX * durationInSec) / width)
        }
    }

    const seekToOffset = (offsetX: number) => callAtOffset(onSeek, offsetX)

    const offsetFromLeft = (pageX: number) => {
        const rect = wrapperRef.current?.getBoundingClientRect()

        return rect ? pageX - rect.left : 0
    }

    const handleClick = () => {
        if (showCursor) {
            seekToOffset(cursorOffsetX)
        }
    }

    const handleTouchStart: TouchEventHandler<HTMLDivElement> = event => {
        if (!showProgress) {
            return
        }
        const touch = event.touches[0]
        touchStartRef.current = { x: touch.pageX, y: touch.pageY }
        isSwipingRef.current = false
    }

    const handleTouchMove: TouchEventHandler<HTMLDivElement> = event => {
        if (!showProgress || !touchStartRef.current) {
            return
        }
        const touch = event.touches[0]
        const dx = Math.abs(touch.pageX - touchStartRef.current.x)
        const dy = Math.abs(touch.pageY - touchStartRef.current.y)

        if (dy > TAP_MOVE_THRESHOLD && dy > dx) {
            isSwipingRef.current = true
        } else if (dx > TAP_MOVE_THRESHOLD) {
            callAtOffset(onScrub, offsetFromLeft(touch.pageX))
        }
    }

    const handleTouchEnd: TouchEventHandler<HTMLDivElement> = event => {
        if (!showProgress || !touchStartRef.current || isSwipingRef.current) {
            touchStartRef.current = null
            isSwipingRef.current = false

            return
        }
        seekToOffset(offsetFromLeft(event.changedTouches[0].pageX))
        touchStartRef.current = null
        isSwipingRef.current = false
    }

    const isCursorVisible = showCursor && cursorOffsetX >= 0

    return (
        <div className={cn(styles.wrapper, className)}>
            <div
                ref={wrapperRef}
                className={cn(styles.inner, { [styles.interactive]: isInteractive })}
                role="none"
                onClick={isInteractive ? handleClick : undefined}
                onTouchStart={isInteractive ? handleTouchStart : undefined}
                onTouchMove={isInteractive ? handleTouchMove : undefined}
                onTouchEnd={isInteractive ? handleTouchEnd : undefined}
            >
                {isInteractive ? (
                    <>
                        <div
                            className={cn(styles.cursorLine, {
                                [styles.cursorVisible]: isCursorVisible,
                            })}
                            style={{ transform: `translateX(${cursorOffsetX}px)` }}
                        />
                        <div
                            ref={timeLabelRef}
                            className={cn(styles.cursorTime, {
                                [styles.cursorVisible]: isCursorVisible,
                            })}
                            style={{ transform: `translate(${cursorTimePosition}px, -50%)` }}
                        >
                            {formatSeconds(cursorSeconds)}
                        </div>
                    </>
                ) : null}
                <svg className={styles.svg} data-variant={variant} width="100%" height="50%">
                    {showProgress ? <WaveformProgress id={gradientId} progress={progress} /> : null}
                    <path
                        className={cn(styles.path, { [styles.pathDefault]: !showProgress })}
                        stroke={showProgress ? `url(#${gradientId})` : undefined}
                        d={d}
                    />
                </svg>
            </div>
        </div>
    )
}
